import { useMemo, useState } from 'react'
import {
    Grid,
    Button,
    Typography,
    InputAdornment,
    alpha,
    Box,
    IconButton,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import AccountBox from '@mui/icons-material/AccountBox'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Visibility from '@mui/icons-material/Visibility'
import VpnKey from '@mui/icons-material/VpnKey'
import { useTranslation } from 'react-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import OWInput from 'components/OWForm/OWInput'
import { useLoginContext } from 'pages/LoginPage/LoginProvider'
import { LoadingButton } from '@mui/lab'

type TLoginUsername = {
    username: string
    password: string
}

const LoginWithUserName = ({ onCancel }: { onCancel?: () => void }) => {
    const { t } = useTranslation()
    const { loginWithUsername, loginError } = useLoginContext()

    const validationSchema = useMemo(
        () =>
            yup.object({
                username: yup
                    .string()
                    .required(t('login.error.required_username')),
                password: yup
                    .string()
                    .required(t('login.error.required_password')),
            }),
        [t],
    )

    const [showPassword, setShowPassword] = useState<boolean>(false)
    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev)
    }

    const handleMouseDownPassword = (event: any) => {
        event.preventDefault()
    }

    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<{
        username: string
        password: string
    }>({
        defaultValues: {
            username: '',
            password: '',
        },
        resolver: yupResolver(validationSchema),
    })

    const onSubmit: SubmitHandler<TLoginUsername> = async (value) => {
        const trimValue = {
            username: value.username.trim(),
            password: value.password.trim(),
        }
        await loginWithUsername(trimValue)
    }

    return (
        <Grid
            item
            sx={{
                paddingTop: {
                    xs: '2%',
                    md: '50px',
                },
            }}
        >
            <Grid spacing={1} item container direction="column">
                <Grid item container spacing={1} justifyContent="center">
                    <Grid item>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container direction="column" spacing={1}>
                                <Grid item textAlign={'center'}>
                                    <OWInput
                                        disabled={isSubmitting}
                                        control={control}
                                        id="username"
                                        autoComplete="username"
                                        size="medium"
                                        name="username"
                                        variant="filled"
                                        InputProps={{
                                            sx: {
                                                color: '#000',
                                                background: '#fff',
                                            },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountBox
                                                        sx={{
                                                            width: '1.5em',
                                                            height: '1.5em',
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .grey[400],
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            borderRadius: '4px',
                                            width: {
                                                xs: 300,
                                                md: 400,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item textAlign={'center'}>
                                    <OWInput
                                        disabled={isSubmitting}
                                        control={control}
                                        id="password"
                                        name="password"
                                        autoComplete="current-password"
                                        size="medium"
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        variant="filled"
                                        InputProps={{
                                            sx: {
                                                color: '#000',
                                                background: '#fff',
                                            },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <VpnKey
                                                        sx={{
                                                            width: '1.5em',
                                                            height: '1.5em',
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .grey[400],
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        sx={{
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .grey[400],
                                                        }}
                                                        size="small"
                                                        aria-label="toggle password visibility"
                                                        onClick={
                                                            handleClickShowPassword
                                                        }
                                                        onMouseDown={
                                                            handleMouseDownPassword
                                                        }
                                                        edge="end"
                                                    >
                                                        {showPassword ? (
                                                            <Visibility />
                                                        ) : (
                                                            <VisibilityOff />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            borderRadius: '4px',
                                            width: {
                                                xs: 300,
                                                md: 400,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sx={{
                                        mx: 2,
                                    }}
                                >
                                    <Typography component={'span'}>
                                        <Box
                                            color="error.light"
                                            sx={{
                                                width: {
                                                    xs: 300,
                                                    md: 400,
                                                },
                                            }}
                                        >
                                            {t(loginError || '')}
                                        </Box>
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid
                                sx={{ paddingTop: '5px' }}
                                item
                                spacing={1}
                                container
                                direction="row"
                                justifyContent="space-around"
                                alignItems="center"
                            >
                                {onCancel && (
                                    <Grid item xl={3} lg={4} sm={4} md={4}>
                                        <Button
                                            disabled={isSubmitting}
                                            sx={{
                                                background: alpha('#fff', 0),
                                                color: '#fff',
                                                border: '1px solid #fff',
                                                fontSize: '18px',
                                                borderColor: '#fff',
                                                '&:hover': {
                                                    backgroundColor: (theme) =>
                                                        theme.palette.info.main,
                                                    borderColor: (theme) =>
                                                        theme.palette.info.main,
                                                },
                                                '&:active': {
                                                    backgroundColor: (theme) =>
                                                        theme.palette.info.main,
                                                    borderColor: (theme) =>
                                                        theme.palette.info.main,
                                                },
                                            }}
                                            size="large"
                                            fullWidth
                                            color="primary"
                                            onClick={onCancel}
                                            disableElevation
                                        >
                                            {t('login.button.cancel')}
                                        </Button>
                                    </Grid>
                                )}

                                <Grid item xl={9} lg={8} sm={8} md={8}>
                                    <LoadingButton
                                        size="large"
                                        sx={{
                                            backgroundColor: alpha('#fff', 0.2),
                                            color: '#fff',
                                            fontSize: '24px',
                                            borderColor: '#fff',
                                            '&:hover': {
                                                backgroundColor: (theme) =>
                                                    theme.palette.info.main,
                                                borderColor: (theme) =>
                                                    theme.palette.info.main,
                                            },
                                            '&:active': {
                                                backgroundColor: (theme) =>
                                                    theme.palette.info.main,
                                                borderColor: (theme) =>
                                                    theme.palette.info.main,
                                            },
                                        }}
                                        fullWidth
                                        color="primary"
                                        type="submit"
                                        disableElevation
                                        loading={isSubmitting}
                                    >
                                        {t('login.button.signin')}
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        </form>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default LoginWithUserName
